import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { AbyssCommonContent } from "../../../../modules/content/components/AbyssCommonContent/AbyssCommonContent";
import { AbyssMedia } from "../../../../modules/content/components/AbyssMedia/AbyssMedia";
import { AbyssRichText } from "../../../../modules/content/components/AbyssRichText/AbyssRichText";
import { AbyssSection } from "../../../../modules/content/components/AbyssSection/AbyssSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <AbyssSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <AbyssCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        {testimonial ? (
          <figure className="abyss-instrument-frame grid overflow-hidden rounded-xl border border-iron bg-obsidian text-frost shadow-[0_28px_80px_rgba(0,0,0,0.32)] md:grid-cols-[minmax(18rem,0.8fr)_1.2fr]">
            {testimonial.portrait ? (
              <AbyssMedia
                aspectRatio="1:1"
                className="h-full"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 35vw, 100vw"
              />
            ) : null}
            <div
              className={cn(
                "flex flex-col justify-center p-7 md:p-12 lg:p-16",
                !testimonial.portrait && "md:col-span-full",
              )}
            >
              <blockquote>
                <AbyssRichText
                  className="font-abyss-body text-3xl leading-tight text-frost md:text-4xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-abyss-mono text-sm font-medium">{testimonial.name}</p>
                {testimonial.attribution ? (
                  <p className="mt-2 font-abyss-mono text-xs text-fog">{testimonial.attribution}</p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="mt-6 inline-flex min-h-11 items-center self-start font-abyss-mono text-xs text-cyan-trace underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </AbyssSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
